import { useState, useMemo, useRef } from 'react';
import { faChartLineUp, faTrophy, faVideo, faPenClip, faSearch, faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormInput, FormSelect, SelectOption, TabList } from '@tryhackme/thm-ui-components';
import { useDebouncedCallback } from 'use-debounce';
import { useAppSelector } from 'src/app/hooks';
import { useFeatureIsOn } from 'src/common/growthbook';
import { ComplexRoomBoxTypes, ScoreboardDataWithPagination } from './complex-room-box.types';
import { Chart, ScoreBoard, Video, WriteUps } from './components';
import { selectOptions } from './complex-room-box.constants';
import { StyledTabs, StyledTab, StyledComplexRoomBox, StyledTabPanel, StyledSelect, StyledViewWrapper, StyledTabsWrapper, StyledSearchBar, StyledClearButton } from './complex-room-box.styles';
export const ComplexRoomBox = ({
  roomDetails,
  tasksDetails,
  scoreboardDetails,
  isVideoVisibleToUser,
  isWriteUpsVisible = true,
  isModuleScoreboard = false,
  isScoreboardLoading = false
}: ComplexRoomBoxTypes) => {
  const isSearchFFOn = useFeatureIsOn('team-scoreboard-with-search');
  const isSimpleRoom = !!roomDetails.simpleRoom;
  const isTeamsRoom = !!roomDetails.teams;
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const {
    tutorPanelOpen,
    vmPanelOpen
  } = useAppSelector(state => state.splitScreen);
  const splitScreenOpen = tutorPanelOpen || vmPanelOpen;
  const options = useMemo(() => {
    const ignoreList: string[] = [];
    if (isSimpleRoom) ignoreList.push('Chart');
    if (!isVideoVisibleToUser) ignoreList.push('Video');
    if (!isWriteUpsVisible) ignoreList.push('Write-ups');
    return selectOptions.filter(option => !ignoreList.includes(option.label));
  }, [isSimpleRoom, isVideoVisibleToUser, isWriteUpsVisible]);
  const [selectedOption, setSelectedOption] = useState<SelectOption | undefined>(options[0]);
  const {
    label
  } = selectedOption ?? {};
  const handleSelection = (option?: SelectOption) => {
    if (option?.label) {
      setSelectedOption(() => ({
        value: option.value,
        label: option.label,
        id: option.id,
        icon: option.icon
      }));
    }
  };
  const handleSearchValue = useDebouncedCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const trimmedValue = e.target.value.trim();
    setSearchTerm(trimmedValue);
    setHasStartedTyping(true);
  }, 350);
  const handleClearClick = () => {
    if (searchRef.current) {
      searchRef.current.value = '';
    }
    setSearchTerm('');
    setHasStartedTyping(false);
  };
  if (!roomDetails) {
    return null;
  }
  const chartDetials = roomDetails?.teams ? ((scoreboardDetails as unknown) as ScoreboardDataWithPagination)?.docs : scoreboardDetails;
  return <StyledComplexRoomBox $splitScreenOpen={splitScreenOpen} data-sentry-element="StyledComplexRoomBox" data-sentry-component="ComplexRoomBox" data-sentry-source-file="complex-room-box.tsx">
      <StyledTabs defaultValue="chart" $splitScreenOpen={splitScreenOpen} data-sentry-element="StyledTabs" data-sentry-source-file="complex-room-box.tsx">
        <StyledTabsWrapper data-sentry-element="StyledTabsWrapper" data-sentry-source-file="complex-room-box.tsx">
          <TabList alignment="left" data-sentry-element="TabList" data-sentry-source-file="complex-room-box.tsx">
            {!isSimpleRoom && <StyledTab value="chart" onClick={() => setIsSearchVisible(false)}>
                <FontAwesomeIcon icon={faChartLineUp} /> Chart
              </StyledTab>}
            <StyledTab value="scoreboard" onClick={() => setIsSearchVisible(true)} data-sentry-element="StyledTab" data-sentry-source-file="complex-room-box.tsx">
              <FontAwesomeIcon icon={faTrophy} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="complex-room-box.tsx" /> Scoreboard
            </StyledTab>
            {isVideoVisibleToUser && <StyledTab value="video" onClick={() => setIsSearchVisible(false)}>
                <FontAwesomeIcon icon={faVideo} /> Video
              </StyledTab>}
            {isWriteUpsVisible && <StyledTab value="write-ups" onClick={() => setIsSearchVisible(false)}>
                <FontAwesomeIcon icon={faPenClip} /> Write-ups
              </StyledTab>}
          </TabList>
          {isSearchFFOn && isTeamsRoom && isSearchVisible && <StyledSearchBar>
              <FormInput placeholder="Search for team by name or keyword…" leftAdornment={<FontAwesomeIcon icon={faSearch} />} rightAdornment={hasStartedTyping || searchTerm?.length >= 1 ? <StyledClearButton data-testid="clear-btn" variant="text" color="secondary" onClick={handleClearClick}>
                      <FontAwesomeIcon icon={faClose} />
                    </StyledClearButton> : <span />} onChange={handleSearchValue} defaultValue={searchTerm} ref={searchRef} maxLength={20} />
            </StyledSearchBar>}
        </StyledTabsWrapper>

        {!isSimpleRoom && <StyledTabPanel value="chart">
            <Chart chartDetails={chartDetials} isLoading={isScoreboardLoading} />
          </StyledTabPanel>}
        <StyledTabPanel value="scoreboard" data-sentry-element="StyledTabPanel" data-sentry-source-file="complex-room-box.tsx">
          <ScoreBoard isLoading={isScoreboardLoading} tasksDetails={tasksDetails} scoreboardDetails={scoreboardDetails} roomDetails={roomDetails} isModuleScoreboard={isModuleScoreboard} searchTerm={searchTerm} data-sentry-element="ScoreBoard" data-sentry-source-file="complex-room-box.tsx" />
        </StyledTabPanel>
        {isVideoVisibleToUser && <StyledTabPanel value="video">
            <Video videoDetails={roomDetails?.video} />
          </StyledTabPanel>}
        {isWriteUpsVisible && <StyledTabPanel value="write-ups">
            <WriteUps roomDetails={roomDetails} />
          </StyledTabPanel>}
      </StyledTabs>

      {/* Only mobile */}
      <StyledSelect $splitScreenOpen={splitScreenOpen} data-sentry-element="StyledSelect" data-sentry-source-file="complex-room-box.tsx">
        <FormSelect options={options} handleSelection={handleSelection} selected={selectedOption} isSearchable={false} data-sentry-element="FormSelect" data-sentry-source-file="complex-room-box.tsx" />
        <StyledViewWrapper data-sentry-element="StyledViewWrapper" data-sentry-source-file="complex-room-box.tsx">
          {!isSimpleRoom && label === 'Chart' && <Chart chartDetails={chartDetials} isLoading={isScoreboardLoading} />}
          {label === 'Scoreboard' && <ScoreBoard tasksDetails={tasksDetails} scoreboardDetails={scoreboardDetails} roomDetails={roomDetails} isModuleScoreboard={isModuleScoreboard} searchTerm={searchTerm} />}
          {label === 'Video' && isVideoVisibleToUser && <Video videoDetails={roomDetails?.video} />}
          {label === 'Write-ups' && isWriteUpsVisible && <WriteUps roomDetails={roomDetails} />}
        </StyledViewWrapper>
      </StyledSelect>
    </StyledComplexRoomBox>;
};